import { createSelector } from '@reduxjs/toolkit';
import {
  getDeviceWithLoadingFromProps,
  RouteComponentPropsDevice,
} from '../../commander/selectors';
import { RootState } from '../../store.model';
import { ZipDeviceControlUnit } from '../controlUnit.model';
import { RouterComponentProps } from '../../../util/route-dom';
import { ControlUnitAPIResponse } from '../api/ControlUnit.model';
import ControlUnitAPI from '../api/ControlUnitAPI';
import { isNotNullOrUndefined } from '../../../util/types/Array';

export type RouteComponentPropsControlUnit = Pick<
  RouterComponentProps<{
    controlUnitId?: string;
  }>,
  'params'
>;

export const getControlUnitQueryParam = (
  _: RootState,
  props: RouteComponentPropsControlUnit
) => props.params.controlUnitId;

export const getControlUnitSelectedByQueryParamSelector = createSelector(
  [
    getControlUnitQueryParam,
    (state: RootState, props: RouteComponentPropsDevice) =>
      ControlUnitAPI.endpoints.getControlUnitById.select(
        props.params.deviceId ?? ''
      )(state),
  ],
  (controlUnitId, dictionaryControlUnit) => {
    return dictionaryControlUnit.data?.entities?.[controlUnitId ?? ''];
  }
);

const getControlUnitsResponseFromDeviceIdProps = createSelector(
  [
    (state: RootState, props: { deviceId: string }) => props.deviceId,
    (state: RootState) => state,
  ],
  (deviceId, state) =>
    ControlUnitAPI.endpoints.getControlUnitById.select(deviceId)(state)
);

export const getDeviceControlUnitsById = createSelector(
  [getControlUnitsResponseFromDeviceIdProps],
  (controlUnitResponse) => {
    return controlUnitResponse?.data?.ids
      .map((controlUnitId) => controlUnitResponse.data?.entities[controlUnitId])
      .filter(isNotNullOrUndefined);
  }
);

export const getControlUnitById = createSelector(
  [
    getDeviceControlUnitsById,
    (_state, props: { controlUnitId: string | number }) => props.controlUnitId,
  ],
  (controlUnits, controlUnitId) =>
    controlUnits?.find(
      (controlUnitCurrent) => controlUnitCurrent.id == controlUnitId
    )
);

export const getControlUnitsActiveByIdFilter = (
  controlUnit: ControlUnitAPIResponse
): boolean => {
  return (
    controlUnit.state === 'ACTIVE' ||
    controlUnit.detailedState === 'INSTALLED' ||
    // Locally disconnected (but not uninstalled)
    controlUnit.detailedState === 'DISCONNECTED'
  );
};

export const getActiveAndVisibleControlUnitsByIdFilter = (
  controlUnit: ControlUnitAPIResponse
): boolean => {
  return (
    getControlUnitsActiveByIdFilter(controlUnit) &&
    getControlUnitsVisibleByIdFilter(controlUnit)
  );
};

export const getControlUnitsActiveByIdSelector = createSelector(
  [getDeviceControlUnitsById],
  (controlUnits) => {
    return controlUnits?.filter(getControlUnitsActiveByIdFilter);
  }
);

export const getControlUnitsVisibleByIdFilter = (
  controlUnit: ControlUnitAPIResponse
): boolean => {
  return (
    // TODO: Please use another way to identify CommanderCU
    controlUnit.id != '1002'
  );
};
export const getControlUnitsVisibleByIdSelector = createSelector(
  [getControlUnitsActiveByIdSelector],
  (controlUnits) => {
    return controlUnits?.filter(getControlUnitsVisibleByIdFilter);
  }
);

export const getControlUnitIdsActiveById = createSelector(
  [getControlUnitsActiveByIdSelector],
  (controlUnits) => controlUnits?.map((controlUnit) => controlUnit.id)
);

export const getZipControlUnitsByDeviceIds = (
  state: RootState,

  props: { devicesIds: string[]; hideCommander?: boolean }
): ZipDeviceControlUnit[] => {
  const getControlUnitsSelector = props.hideCommander
    ? getControlUnitsVisibleByIdSelector
    : getControlUnitsActiveByIdSelector;
  return props.devicesIds.map((deviceId) => {
    const controlUnits = getControlUnitsSelector(state, {
      deviceId,
    });
    const response =
      ControlUnitAPI.endpoints.getControlUnitById.select(deviceId)(state);
    const deviceName = getDeviceWithLoadingFromProps(state, { deviceId }).name;
    return {
      deviceId,
      deviceName,
      controlUnits,
      ...response,
      loading: response.isLoading,
      error: response.error,
    } as ZipDeviceControlUnit;
  });
};
